import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { createClient } from '@kairos/supabase/browser'

import {
  AuthErrorBanner,
  AuthField,
  AuthSubmit,
  AuthTitle,
} from '~/components/auth-shell/AuthFormPrimitives'

/**
 * `/authentication/signup` — invite-only admin signup.
 *
 * Per `02-authentication.md` §2 the public signup is gated:
 *   - No `?token=`            → render the calm "invite-only" empty state
 *   - With `?token=` (+email) → render the activation form (full name +
 *                               password) and consume the invite via
 *                               `verifyOtp({ type: 'invite', token })`
 *
 * The legacy Next admin app uses a custom `register_user_from_invitation`
 * RPC; the SPA route here uses Supabase's first-party invite flow because
 * the admin-dashboard talks to the Worker over a stateless API and we don't
 * want to recreate the RPC dependency. If the ops team needs the legacy
 * RPC path instead, swap `verifyOtp` + `updateUser` for a single RPC call
 * (see HUMAN-FLAG in `authentication.set-password.tsx`).
 */

const searchSchema = z.object({
  token: z.string().optional(),
  email: z.string().email().optional(),
})

const activationSchema = z
  .object({
    fullName: z.string().min(1, 'Enter your full name.'),
    password: z
      .string()
      .min(12, 'Password must be at least 12 characters.')
      .regex(/[A-Z]/, 'Include at least one uppercase letter.')
      .regex(/[0-9]/, 'Include at least one number.'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'],
  })

type ActivationInput = z.infer<typeof activationSchema>

export const Route = createFileRoute('/authentication/signup')({
  validateSearch: searchSchema,
  component: SignupPage,
})

function SignupPage(): ReactNode {
  const search = Route.useSearch()
  return (
    <>
      {search.token ? (
        <ActivateForm token={search.token} email={search.email} />
      ) : (
        <InviteOnlyEmptyState />
      )}
    </>
  )
}

function InviteOnlyEmptyState(): ReactNode {
  return (
    <>
      <AuthTitle
        title="Admin signup is invite-only"
        subtitle="Ask your platform owner for an invite — admins are created via invitation."
      />
      <Link to="/authentication/login" className="ka-auth-block-link">
        Back to sign in
      </Link>
    </>
  )
}

interface ActivateFormProps {
  readonly token: string
  readonly email: string | undefined
}

function ActivateForm({ token, email }: ActivateFormProps): ReactNode {
  const navigate = useNavigate()
  const [bannerError, setBannerError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ActivationInput>({
    resolver: zodResolver(activationSchema),
  })

  const onSubmit = async (values: ActivationInput): Promise<void> => {
    setBannerError(null)
    if (!email) {
      setBannerError(
        'Activation link is missing the email address. Use the link from your invitation email.',
      )
      return
    }

    const supabase = createClient()

    // Consume the invite token. Supabase's invite flow uses a one-time token
    // delivered as `?token=` in the email link; `verifyOtp` with `type:
    // 'invite'` exchanges it for a session.
    const { error: verifyError } = await supabase.auth.verifyOtp({
      type: 'invite',
      token,
      email,
    })
    if (verifyError) {
      setBannerError(verifyError.message)
      return
    }

    // Once the session is established, set the password + display name on
    // the user record so the next sign-in works.
    const { error: updateError } = await supabase.auth.updateUser({
      password: values.password,
      data: { full_name: values.fullName },
    })
    if (updateError) {
      setBannerError(updateError.message)
      return
    }

    void navigate({ to: '/admin' })
  }

  return (
    <>
      <AuthTitle
        title="Welcome to Kairos Admin"
        subtitle="Set your password to activate your account."
      />
      {bannerError ? <AuthErrorBanner>{bannerError}</AuthErrorBanner> : null}
      <form noValidate onSubmit={handleSubmit(onSubmit)} method="post" className="ka-stack">
        {email ? (
          <AuthField
            id="signup-email"
            label="Email"
            type="email"
            readOnly
            inputProps={{ value: email, readOnly: true }}
          />
        ) : null}
        <AuthField
          id="signup-full-name"
          label="Full name"
          type="text"
          autoComplete="name"
          error={errors.fullName?.message}
          inputProps={register('fullName')}
        />
        <AuthField
          id="signup-password"
          label="New password"
          type="password"
          autoComplete="new-password"
          helper="At least 12 characters with one uppercase letter and one number."
          error={errors.password?.message}
          inputProps={register('password')}
        />
        <AuthField
          id="signup-confirm-password"
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          error={errors.confirmPassword?.message}
          inputProps={register('confirmPassword')}
        />
        <AuthSubmit loading={isSubmitting}>Activate account</AuthSubmit>
      </form>
    </>
  )
}
